// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// store
import { changeVehicleCreators, changeVehicleSelectors, middlewareMgr, newVehicleSelected } from '../../store';

// analytics
import { createAiVehicleOptionsAccordionEvent, createAiVehicleOptionsDisplayedEvent } from '../../analytics';

// hooks
import { useAddNewScenarioAvailability } from './useAddNewScenarioAvailability';

// utils
import { useValidateVehicle } from '../../utils';
import { mapAiAltVehicleOptionToVehicle } from '../../utils/mapAiAltVehicleOptionToVehicle';

// types
import type { AiAltVehicleOption } from '../../types/AiAltVehicleOption';

const MAX_RECOMMENDATION_CARDS = 3;

const AUTO_COLLAPSE_DELAY_MS = 5000;

export const useAiAltVehicleOptions = () => {
    const dispatch = useDispatch();
    const recommendations = useSelector(changeVehicleSelectors.getAiVehicleRecommendations);
    const isLoading = useSelector(changeVehicleSelectors.getAiVehicleRecommendationsLoading);
    const selectedVehicle = useSelector(changeVehicleSelectors.getSelectedVehicleWithCustomData);
    const currentVehicleVin = useSelector(changeVehicleSelectors.getVehicleVin);
    const currentVehicleStockNumber = useSelector(changeVehicleSelectors.getVehicleStockNumber);
    const [isExpanded, setIsExpanded] = useState(true);

    // Freeze the deal-vehicle identity for the AC-3.2 exclusion at screen-open. Clicking "Change"
    // optimistically swaps the deal vehicle (SWITCH_VEHICLE), so reading it live would filter the
    // just-picked card out and reshuffle mid-interaction; re-open remounts and re-captures.
    const exclusionBaselineRef = useRef<{ vin?: string; stockNumber?: string }>({
        vin: currentVehicleVin,
        stockNumber: currentVehicleStockNumber
    });
    // Defensive: if the deal vehicle wasn't resolved on the first render, capture it once it is (still
    // before any Change can happen), then hold it steady for the rest of this mount.
    if (exclusionBaselineRef.current.vin === undefined && exclusionBaselineRef.current.stockNumber === undefined) {
        exclusionBaselineRef.current = { vin: currentVehicleVin, stockNumber: currentVehicleStockNumber };
    }
    const primaryVin = exclusionBaselineRef.current.vin;
    const primaryStockNumber = exclusionBaselineRef.current.stockNumber;

    // Fire the AVO fetch once when the section opens (mount == screen-open); the host decides via
    // dispatchFetchIfStale whether a fetch is actually needed.
    useEffect(() => {
        changeVehicleSelectors.dispatchFetchIfStale();
    }, []);

    // The recommendation engine is seeded from the current deal vehicle and does not self-exclude it,
    // so drop it here (by vin, falling back to stockNumber) before capping — AC-3.2 / STEP-21.
    const isPrimaryVehicle = useCallback(
        (recommendation: AiAltVehicleOption): boolean =>
            (!!primaryVin && recommendation.vehicle.vin === primaryVin) ||
            (!!primaryStockNumber && recommendation.vehicle.stockNumber === primaryStockNumber),
        [primaryVin, primaryStockNumber]
    );

    const cards = useMemo(
        () => recommendations.filter((recommendation) => !isPrimaryVehicle(recommendation)).slice(0, MAX_RECOMMENDATION_CARDS),
        [recommendations, isPrimaryVehicle]
    );
    const autoCollapseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const autoCollapseSettled = useRef(false);
    const hasCards = cards.length > 0;

    useEffect(() => {
        if (!hasCards || autoCollapseSettled.current) return;

        autoCollapseSettled.current = true;
        autoCollapseTimer.current = setTimeout(() => {
            autoCollapseTimer.current = undefined;
            setIsExpanded(false);
        }, AUTO_COLLAPSE_DELAY_MS);
    }, [hasCards]);

    useEffect(
        () => () => {
            if (autoCollapseTimer.current) clearTimeout(autoCollapseTimer.current);
        },
        []
    );

    const displayedVinsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        cards.forEach((recommendation) => {
            const { vin } = recommendation.vehicle;
            if (displayedVinsRef.current.has(vin)) return;
            displayedVinsRef.current.add(vin);
            dispatch(middlewareMgr.hostActions.forwardCDLEvent(createAiVehicleOptionsDisplayedEvent(recommendation).payload));
        });
    }, [cards, dispatch]);

    const toggleExpanded = () => {
        if (autoCollapseTimer.current) {
            clearTimeout(autoCollapseTimer.current);
            autoCollapseTimer.current = undefined;
        }
        autoCollapseSettled.current = true;
        const willExpand = !isExpanded;
        if (willExpand) {
            dispatch(middlewareMgr.hostActions.forwardCDLEvent(createAiVehicleOptionsAccordionEvent(selectedVehicle).payload));
        }
        setIsExpanded(willExpand);
    };
    const selectVehicle = (recommendation: AiAltVehicleOption) => {
        dispatch(newVehicleSelected(mapAiAltVehicleOptionToVehicle(recommendation)));
    };
    const isSelected = (recommendation: AiAltVehicleOption) => selectedVehicle?.vin === recommendation.vehicle.vin;

    return { cards, isLoading, isExpanded, toggleExpanded, selectVehicle, isSelected };
};

/**
 * Inline "Select Vehicle" / "New Scenario" state for one Alternative Vehicle Options card.
 *
 * Deliberately the same readings the result row makes (`useVehicleListRowItemInStock`), so a card and a row can never
 * disagree about the same vehicle: the same validity check against the selected vehicle, the same
 * scenario limit, and the same request-the-footer handlers. Card-independent, so which card shows the actions stays
 * `isSelected`'s job in the card itself.
 */
export const useAiAltVehicleOptionsCardActions = () => {
    const dispatch = useDispatch();
    const isAiAltVehicleOptionsActive = useSelector(changeVehicleSelectors.isAiAltVehicleOptionsActive);
    const isChangeProcessing = useSelector(changeVehicleSelectors.isChangeProcessing);
    const selectedVehicleWithCustomData = useSelector(changeVehicleSelectors.getSelectedVehicleWithCustomData);

    // The buttons act on the selected vehicle, so they are judged on it rather than on this card's own recommendation —
    // otherwise a button could say one thing while the click did another. Indeterminate validity leaves them disabled.
    const isInlineActionsDisabled = !useValidateVehicle(selectedVehicleWithCustomData);

    // The limit is shared with the footer and the rows so none of them can disagree about it.
    const { canAddNewScenario, isScenarioLimitReached } = useAddNewScenarioAvailability();

    // The card only asks; the footer applies, because only it knows whether this host confirms first. The card is
    // already selected by the time the buttons show, so there is nothing left to select.
    const handleSelectVehicleClick = () => {
        dispatch(changeVehicleCreators.setApplyVehicleRequested(true));
    };

    // Creating the scenario ends with the dealer back on the dashboard, and only the footer knows whether that means
    // closing a slideout or navigating — the same split as the apply above.
    const handleAddNewScenarioClick = () => {
        dispatch(changeVehicleCreators.setAddNewScenarioRequested(true));
    };

    return {
        isAiAltVehicleOptionsActive,
        isInlineActionsDisabled,
        isChangeProcessing,
        canAddNewScenario,
        isAddNewScenarioDisabled: isInlineActionsDisabled || isScenarioLimitReached,
        handleSelectVehicleClick,
        handleAddNewScenarioClick
    };
};
